import {
  DataInterfaceImplementation,
  HandleWorkspaceDeleted,
  ResetWithScopeData,
  ClearCollections,
  AddScopeData,
  HandleWorkspaceCreatedTransaction,
} from '@ardoq/data-interface';
import { toIds } from '@ardoq/core';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  addComponentsAction,
  removeComponentsAction,
  resetComponentsAction,
  resetComponentsWithData,
} from 'streams/components/ComponentActions';
import {
  removeWorkspaceTags,
  resetTags,
  resetTagsWithData,
} from 'streams/tags/actions';
import { resetDocumentArchiveAttachments } from 'streams/documentArchive/actions';
import { currentUserInterface } from 'modelInterface/currentUser/currentUserInterface';
import { addPermissionForResource } from 'streams/currentUserPermissions/actions';
import {
  addWorkspaces,
  resetWorkspaces,
  resetWorkspacesWithData,
} from 'streams/workspaces/actions';
import { workspaceInterface } from '@ardoq/workspace-interface';
import {
  addModels,
  resetModels,
  resetModelsWithData,
} from 'streams/models/actions';
import {
  addFields,
  resetFields,
  resetFieldsWithData,
} from 'streams/fields/actions';
import {
  addReferences,
  batchRemoveReferences,
  resetReferences,
  resetReferencesWithData,
} from 'streams/references/ReferenceActions';
import { referenceInterface } from '@ardoq/reference-interface';
import { componentInterface } from '@ardoq/component-interface';
import { resetGraph } from 'modelInterface/graphModelActions';

const handleWorkspaceDeleted: HandleWorkspaceDeleted = workspaceId => {
  const referencesToRemove =
    referenceInterface.getReferencesInRootWorkspace(workspaceId);
  dispatchAction(batchRemoveReferences(toIds(referencesToRemove)));

  const componentIds = toIds(
    componentInterface.getComponentsByWorkspaceId(workspaceId)
  );
  dispatchAction(removeComponentsAction({ ids: componentIds }));

  dispatchAction(removeWorkspaceTags({ workspaceId }));
};

const resetWithScopeData: ResetWithScopeData = (
  scopeData,
  options = { silent: true }
) => {
  dispatchAction(resetTagsWithData({ tags: scopeData.tags }));
  dispatchAction(resetFieldsWithData(scopeData.fields));
  dispatchAction(resetModelsWithData(scopeData.models));

  dispatchAction(
    resetWorkspacesWithData({
      workspaces: scopeData.workspaces,
      silent: options.silent,
    })
  );
  dispatchAction(resetComponentsWithData({ components: scopeData.components }));
  dispatchAction(resetGraph());
  dispatchAction(resetReferencesWithData(scopeData.references));

  if (scopeData.attachments) {
    dispatchAction(resetDocumentArchiveAttachments(scopeData.attachments));
  }
};

const clearCollections: ClearCollections = () => {
  dispatchAction(resetReferences());
  dispatchAction(resetComponentsAction());
  dispatchAction(resetGraph());
  dispatchAction(resetTags());
  dispatchAction(resetFields());
  dispatchAction(resetModels());
  dispatchAction(resetWorkspaces());
};

const addScopeData: AddScopeData = ({
  workspaces,
  models,
  fields,
  components,
  references,
}) => {
  dispatchAction(addModels(models));
  dispatchAction(addWorkspaces({ workspaces }));
  dispatchAction(addFields({ fields }));
  dispatchAction(addComponentsAction({ components }));
  dispatchAction(addReferences({ references }));
};

const handleWorkspaceCreatedTransaction: HandleWorkspaceCreatedTransaction = ({
  model,
  workspace,
  permission,
  fields,
}) => {
  if (workspaceInterface.workspaceExists(workspace._id)) {
    return;
  }

  const currentUser = currentUserInterface.getCurrentUserAttributesClone();
  dispatchAction(addPermissionForResource({ permission, user: currentUser }));
  dispatchAction(addModels([model]));
  dispatchAction(addWorkspaces({ workspaces: [workspace] }));
  dispatchAction(addFields({ fields }));
};

/**
 * `dataInterface` is the write side of the Backbone-era model interface layer:
 * every member mutates the global collections imperatively.
 *
 * The app is stream-based now. Dispatch the rxbeach action that owns the change
 * and let the collection routines reduce it, rather than reaching for these
 * mutators from arbitrary code.
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe.
 */
export const dataInterface: DataInterfaceImplementation = {
  /**
   * @deprecated Dispatch the workspace-deleted action and let the collection routines reduce it.
   */
  handleWorkspaceDeleted,
  /**
   * @deprecated Dispatch the scope-data-loaded action and let the collection routines reduce it.
   */
  resetWithScopeData,
  /**
   * @deprecated Dispatch the action that owns clearing the collections and let the collection routines reduce it.
   */
  clearCollections,
  /**
   * @deprecated Dispatch the scope-data-loaded action and let the collection routines reduce it.
   */
  addScopeData,
  /**
   * @deprecated Dispatch the workspace-created action and let the collection routines reduce it.
   */
  handleWorkspaceCreatedTransaction,
};
